import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { Outcome, OutcomeAtiBasis, OutcomeStatus } from '../outcomes.model';

export interface OutcomeDialogResult {
  patch: Partial<Outcome>;
  currentUser: string;
}

const STATUS_OPTIONS: { label: string; value: OutcomeStatus }[] = [
  { label: 'Verified', value: 'verified' },
  { label: 'Unverified', value: 'unverified' },
];

const ATI_BASIS_OPTIONS: { label: string; value: OutcomeAtiBasis }[] = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Annual', value: 'annual' },
  { label: 'One-time', value: 'one_time' },
];

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

type OutcomeForm = {
  description: string;
  tags: string;
  dateAchieved: string;
  atiValue: number | null;
  atiBasis: OutcomeAtiBasis | null;
  linkedActionId: string;
  status: OutcomeStatus;
};

@Component({
  selector: 'bai-outcome-details-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    Button,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TagModule,
    TextareaModule,
  ],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <!-- Origin + metadata -->
      <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <p-tag
          [value]="outcome.sourceType === 'ai' ? 'AI Agent' : 'Human'"
          [severity]="outcome.sourceType === 'ai' ? 'info' : 'secondary'"
          styleClass="text-xs"
        />
        <span>Created by {{ outcome.createdBy }} on {{ outcome.createdAt }}</span>
        @if (outcome.updatedBy !== outcome.createdBy || outcome.updatedAt !== outcome.createdAt) {
          <span>· Last updated by {{ outcome.updatedBy }} on {{ outcome.updatedAt }}</span>
        }
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-desc">
          Outcome <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <textarea
          id="od-desc"
          pTextarea
          [ngModel]="form().description"
          (ngModelChange)="patch('description', $event)"
          rows="3"
          class="w-full"
          aria-required="true"
        ></textarea>
        @if (submitted() && !form().description.trim()) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <!-- Tags -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-tags">
          Tags <span class="text-xs text-gray-400">(comma-separated)</span>
        </label>
        <input
          id="od-tags"
          pInputText
          [ngModel]="form().tags"
          (ngModelChange)="patch('tags', $event)"
          placeholder="e.g. IRA, Tax, Savings"
          class="w-full"
        />
      </div>

      <!-- Date Achieved -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-date">Date Achieved</label>
        <input
          id="od-date"
          pInputText
          type="date"
          [ngModel]="form().dateAchieved"
          (ngModelChange)="patch('dateAchieved', $event)"
          class="w-full"
        />
      </div>

      <!-- ATI Value + Basis -->
      <div class="flex gap-3">
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-gray-700" for="od-ati">ATI Value ($)</label>
          <p-inputnumber
            inputId="od-ati"
            [ngModel]="form().atiValue"
            (ngModelChange)="patch('atiValue', $event)"
            [min]="0"
            [useGrouping]="true"
            placeholder="Optional"
            styleClass="w-full"
          />
          @if (submitted() && atiValueNegative()) {
            <span class="text-xs text-red-500" role="alert">Must be positive</span>
          }
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-sm font-medium text-gray-700" for="od-basis">
            ATI Basis
            @if (atiBasisRequired()) {
              <span class="text-red-500" aria-hidden="true">*</span>
            }
          </label>
          <p-select
            inputId="od-basis"
            [options]="atiBasisOptions"
            [ngModel]="form().atiBasis"
            (ngModelChange)="patch('atiBasis', $event)"
            optionLabel="label"
            optionValue="value"
            placeholder="Select basis"
            styleClass="w-full"
            [disabled]="!hasAtiValue()"
            [showClear]="true"
          />
          @if (submitted() && atiBasisRequired()) {
            <span class="text-xs text-red-500" role="alert">Required when ATI value is set</span>
          }
        </div>
      </div>

      <!-- Status -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-status">Status</label>
        <p-select
          inputId="od-status"
          [options]="statusOptions"
          [ngModel]="form().status"
          (ngModelChange)="patch('status', $event)"
          optionLabel="label"
          optionValue="value"
          styleClass="w-full"
        />
      </div>

      <!-- Time to Outcome (display-only if present) -->
      @if (outcome.timeToOutcomeDays !== null) {
        <div class="text-sm text-gray-500">
          Time to Outcome: <span class="text-gray-800 font-medium">{{ outcome.timeToOutcomeDays }} days</span>
        </div>
      }

      <!-- Footer -->
      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="cancel()" />
        <p-button label="Save" (onClick)="save()" />
      </div>

    </div>
  `,
})
export class OutcomeDetailsDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);

  readonly outcome: Outcome = this._config.data as Outcome;
  readonly statusOptions = STATUS_OPTIONS;
  readonly atiBasisOptions = ATI_BASIS_OPTIONS;
  readonly submitted = signal(false);

  readonly form = signal<OutcomeForm>({
    description: this.outcome.description,
    tags: this.outcome.tags.join(', '),
    dateAchieved: this.outcome.dateAchieved,
    atiValue: this.outcome.atiValue,
    atiBasis: this.outcome.atiBasis,
    linkedActionId: this.outcome.linkedActionId ?? '',
    status: this.outcome.status,
  });

  readonly hasAtiValue = computed(() => (this.form().atiValue ?? 0) > 0);
  readonly atiBasisRequired = computed(() => this.hasAtiValue() && !this.form().atiBasis);
  readonly atiValueNegative = computed(() => this.form().atiValue !== null && (this.form().atiValue ?? 0) < 0);

  patch<K extends keyof OutcomeForm>(key: K, value: OutcomeForm[K]): void {
    this.form.update((f) => {
      const updated = { ...f, [key]: value };
      // Clear basis when ATI value is removed
      if (key === 'atiValue' && (value === null || (value as number) <= 0)) {
        updated.atiBasis = null;
      }
      return updated;
    });
  }

  save(): void {
    this.submitted.set(true);
    const f = this.form();
    if (!f.description.trim()) return;
    if (this.atiValueNegative()) return;
    if (this.atiBasisRequired()) return;

    const atiValue = (f.atiValue ?? 0) > 0 ? f.atiValue : null;
    const atiBasis = atiValue !== null ? f.atiBasis : null;

    const patch: Partial<Outcome> = {
      description: f.description.trim(),
      tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
      dateAchieved: f.dateAchieved,
      atiValue,
      atiBasis,
      linkedActionId: f.linkedActionId || null,
      status: f.status,
    };

    this._ref.close({ patch, currentUser: 'FA' } satisfies OutcomeDialogResult);
  }

  cancel(): void {
    this._ref.close(null);
  }

  formatAti(value: number | null): string {
    return value !== null ? USD.format(value) : '—';
  }
}
